(function () {
  class VideoStories {
    constructor(container) {
      if (container.dataset.videoStoriesInit === '1') return;
      container.dataset.videoStoriesInit = '1';

      this.container = container;
      this.track = container.querySelector('[data-video-stories-track]');
      this.circles = container.querySelectorAll('[data-video-stories-trigger]');
      this.prevBtn = container.querySelector('.video-stories__arrow--prev');
      this.nextBtn = container.querySelector('.video-stories__arrow--next');
      this.modal = container.querySelector('[data-video-stories-modal]');
      if (!this.modal) return;
      this.originalParent = this.modal.parentNode;
      this.slides = this.modal.querySelectorAll('[data-story-slide]');
      this.progressBars = this.modal.querySelectorAll('[data-progress-bar]');
      this.modalPrevBtn = this.modal.querySelector('[data-video-stories-prev]');
      this.modalNextBtn = this.modal.querySelector('[data-video-stories-next]');

      this.currentIndex = 0;
      this.isOpen = false;
      this.isMuted = true;
      this.isPaused = false;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.holdTimer = null;
      this.wasHolding = false;

      this.init();
    }

    init() {
      this.circles.forEach((circle, index) => {
        let touchStartX = 0;
        let touchStartY = 0;

        circle.addEventListener('touchstart', (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        }, { passive: true });

        circle.addEventListener('touchend', (e) => {
          const touchEndX = e.changedTouches[0].clientX;
          const touchEndY = e.changedTouches[0].clientY;
          const deltaX = Math.abs(touchEndX - touchStartX);
          const deltaY = Math.abs(touchEndY - touchStartY);

          if (deltaX < 15 && deltaY < 15) {
            e.preventDefault();
            this.openStory(index);
          }
        }, { passive: false });

        circle.addEventListener('click', (e) => {
          e.preventDefault();
          this.openStory(index);
        });

        this.setupPreviewAutoplay(circle);
      });

      if (this.prevBtn && this.nextBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollTrack('prev'));
        this.nextBtn.addEventListener('click', () => this.scrollTrack('next'));
        this.track.addEventListener('scroll', () => this.updateArrowStates());
        this.updateArrowStates();
      }

      this.modal.querySelectorAll('[data-video-stories-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeStory());
      });

      this.modalPrevBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.prevStory();
      });
      this.modalNextBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.nextStory();
      });

      this.modal.querySelectorAll('.video-stories-modal__mute').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.toggleMute();
        });
      });

      document.addEventListener('keydown', (e) => {
        if (!this.isOpen) return;
        if (e.key === 'Escape') this.closeStory();
        if (e.key === 'ArrowLeft') this.prevStory();
        if (e.key === 'ArrowRight') this.nextStory();
        if (e.key === ' ') {
          e.preventDefault();
          this.togglePause();
        }
      });

      this.modal.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.modal.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });

      const videoWrapper = this.modal.querySelector('.video-stories-modal__video-wrapper');
      if (videoWrapper) {
        videoWrapper.addEventListener('mousedown', (e) => this.startHold(e));
        videoWrapper.addEventListener('mouseup', (e) => this.endHold(e));
        videoWrapper.addEventListener('mouseleave', () => this.endHold());
        videoWrapper.addEventListener('touchstart', (e) => this.startHold(e), { passive: true });
        videoWrapper.addEventListener('touchend', (e) => this.endHold(e), { passive: true });
      }
    }

    startHold(e) {
      if (e.target.closest('button')) return;
      this.wasHolding = false;
      this.holdTimer = setTimeout(() => {
        this.wasHolding = true;
        if (!this.isPaused) {
          this.togglePause();
        }
      }, 200);
    }

    endHold() {
      if (this.holdTimer) {
        clearTimeout(this.holdTimer);
        this.holdTimer = null;
      }
      if (this.wasHolding && this.isPaused) {
        this.togglePause();
        this.wasHolding = false;
      }
    }

    setupPreviewAutoplay(circle) {
      const video = circle.querySelector('video');
      if (!video) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.load();
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.5 });

      observer.observe(circle);
    }

    scrollTrack(direction) {
      const scrollAmount = this.track.clientWidth * 0.8;
      const newScroll = direction === 'next'
        ? this.track.scrollLeft + scrollAmount
        : this.track.scrollLeft - scrollAmount;

      this.track.scrollTo({ left: newScroll, behavior: 'smooth' });
    }

    updateArrowStates() {
      if (!this.prevBtn || !this.nextBtn) return;

      const isAtStart = this.track.scrollLeft <= 10;
      const isAtEnd = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 10;

      this.prevBtn.disabled = isAtStart;
      this.nextBtn.disabled = isAtEnd;
    }

    updateModalArrows() {
      if (this.modalPrevBtn) {
        this.modalPrevBtn.classList.toggle('is-hidden', this.currentIndex === 0);
      }
      if (this.modalNextBtn) {
        this.modalNextBtn.classList.toggle('is-hidden', this.currentIndex === this.slides.length - 1);
      }
    }

    openStory(index) {
      this.currentIndex = index;
      this.isOpen = true;
      this.isPaused = false;

      if (typeof pauseAllMedia === 'function') {
        pauseAllMedia();
      }

      this.circles.forEach((circle) => {
        const video = circle.querySelector('video');
        if (video) video.pause();
      });

      document.body.appendChild(this.modal);
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');

      this.goToSlide(index);
      this.updateModalArrows();
      this.modal.querySelector('.video-stories-modal__close')?.focus();
    }

    closeStory() {
      this.isOpen = false;

      const currentSlide = this.slides[this.currentIndex];
      const video = currentSlide?.querySelector('video');
      if (video) {
        video.pause();
        video.currentTime = 0;
        if (this.boundTimeUpdate) {
          video.removeEventListener('timeupdate', this.boundTimeUpdate);
        }
        if (this.boundEnded) {
          video.removeEventListener('ended', this.boundEnded);
        }
      }

      this.modal.setAttribute('aria-hidden', 'true');
      this.modal.classList.remove('is-paused', 'is-unmuted');
      document.body.classList.remove('overflow-hidden');

      this.originalParent.appendChild(this.modal);

      this.progressBars.forEach((bar) => {
        bar.classList.remove('is-active', 'is-completed');
        const fill = bar.querySelector('.video-stories-modal__progress-fill');
        if (fill) fill.style.width = '0%';
      });

      this.circles.forEach((circle) => {
        const v = circle.querySelector('video');
        if (v) {
          const rect = circle.getBoundingClientRect();
          if (rect.left < window.innerWidth && rect.right > 0) {
            v.play().catch(() => {});
          }
        }
      });
    }

    goToSlide(index) {
      const prevSlide = this.modal.querySelector('.video-stories-modal__slide.is-active');
      const prevVideo = prevSlide?.querySelector('video');
      if (prevVideo) {
        prevVideo.pause();
        prevVideo.currentTime = 0;
        if (this.boundTimeUpdate) {
          prevVideo.removeEventListener('timeupdate', this.boundTimeUpdate);
        }
        if (this.boundEnded) {
          prevVideo.removeEventListener('ended', this.boundEnded);
        }
      }

      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });

      this.progressBars.forEach((bar, i) => {
        bar.classList.remove('is-active', 'is-completed');
        const fill = bar.querySelector('.video-stories-modal__progress-fill');

        if (i < index) {
          bar.classList.add('is-completed');
          if (fill) fill.style.width = '100%';
        } else if (i === index) {
          bar.classList.add('is-active');
          if (fill) fill.style.width = '0%';
        } else {
          if (fill) fill.style.width = '0%';
        }
      });

      const newSlide = this.slides[index];
      const newVideo = newSlide?.querySelector('video');

      if (newVideo) {
        const self = this;
        const activeBar = this.progressBars[index];
        const fill = activeBar?.querySelector('.video-stories-modal__progress-fill');

        if (this.boundTimeUpdate) {
          newVideo.removeEventListener('timeupdate', this.boundTimeUpdate);
        }
        if (this.boundEnded) {
          newVideo.removeEventListener('ended', this.boundEnded);
        }

        this.boundTimeUpdate = function () {
          const duration = newVideo.duration;
          const currentTime = newVideo.currentTime;

          if (duration && duration > 0 && !isNaN(duration) && fill) {
            const progress = (currentTime / duration) * 100;
            fill.style.width = progress + '%';
          }
        };

        this.boundEnded = function () {
          if (fill) fill.style.width = '100%';
          if (activeBar) {
            activeBar.classList.remove('is-active');
            activeBar.classList.add('is-completed');
          }

          if (index < self.slides.length - 1) {
            self.nextStory();
          } else {
            self.closeStory();
          }
        };

        newVideo.addEventListener('timeupdate', this.boundTimeUpdate);
        newVideo.addEventListener('ended', this.boundEnded);

        newVideo.muted = true;
        newVideo.currentTime = 0;

        const attemptPlay = () => {
          newVideo.play().then(() => {
            newVideo.muted = self.isMuted;
          }).catch(() => {});
        };

        if (newVideo.readyState >= 3) {
          attemptPlay();
        } else {
          newVideo.addEventListener('canplay', attemptPlay, { once: true });
          newVideo.addEventListener('loadeddata', () => {
            if (newVideo.paused) attemptPlay();
          }, { once: true });
          newVideo.load();
        }
      }

      this.currentIndex = index;
      this.updateModalArrows();
    }

    prevStory() {
      if (this.currentIndex > 0) {
        this.goToSlide(this.currentIndex - 1);
      }
    }

    nextStory() {
      if (this.currentIndex < this.slides.length - 1) {
        this.goToSlide(this.currentIndex + 1);
      } else {
        this.closeStory();
      }
    }

    toggleMute() {
      this.isMuted = !this.isMuted;
      this.modal.classList.toggle('is-unmuted', !this.isMuted);

      const currentSlide = this.slides[this.currentIndex];
      const video = currentSlide?.querySelector('video');
      if (video) {
        video.muted = this.isMuted;
      }
    }

    togglePause() {
      this.isPaused = !this.isPaused;
      this.modal.classList.toggle('is-paused', this.isPaused);

      const currentSlide = this.slides[this.currentIndex];
      const video = currentSlide?.querySelector('video');
      if (video) {
        if (this.isPaused) {
          video.pause();
        } else {
          video.play().catch(() => {});
        }
      }
    }

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
    }

    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;
      const diffX = this.touchStartX - touchEndX;
      const diffY = this.touchStartY - touchEndY;

      if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
        if (diffX > 0) {
          this.nextStory();
        } else {
          this.prevStory();
        }
      }
    }
  }

  const init = () => {
    document.querySelectorAll('.video-stories').forEach((container) => {
      new VideoStories(container);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
